/**
 * The typed fetch wrapper. Handles cookie auth, CSRF and Idempotency-Key.
 *
 * **Every type in here comes from `schema.d.ts`, which is generated from the
 * server's OpenAPI.** Hand-writing a request or response type is forbidden: a
 * hand-written type drifts from the server, and the first symptom is a blocked
 * action rendering as enabled — which is exactly the failure the API's
 * `GET /cases/{id}/transitions` exists to prevent.
 *
 * Regenerate with `make client` after any change to a router or a schema.
 */

import { asOfHeaders } from '../lib/asOf'
import type { components, paths } from './schema'

export type ApiError = components['schemas']['ApiError']
export type CaseDetail = components['schemas']['CaseDetail']
export type CaseSummary = components['schemas']['CaseSummary']
export type ClockView = components['schemas']['ClockView']
export type DocumentRow = components['schemas']['DocumentRow']
export type TransitionEnvelope = components['schemas']['TransitionEnvelope']
export type TransitionOption = components['schemas']['TransitionOption']
export type TransitionReceipt = components['schemas']['TransitionReceipt']
export type RuleView = components['schemas']['RuleView']
export type ChatPanel = components['schemas']['ChatPanel']
export type ChatMessageView = components['schemas']['ChatMessageView']
export type ChatAnswerView = components['schemas']['ChatAnswerView']
export type CitationView = components['schemas']['CitationView']
export type CitationKind = CitationView['kind']
export type UserView = components['schemas']['UserView']
export type AccountView = components['schemas']['AccountView']
export type CreateAccountBody = Body<'/api/accounts', 'post'>
export type SetPasswordBody = Body<'/api/accounts/{username}/password', 'post'>
export type ChangeOwnPasswordBody = Body<'/api/accounts/me/password', 'post'>
export type ReauthBody = Body<'/api/accounts/{username}/deactivate', 'post'>
export type AuditVerification = components['schemas']['AuditVerification']
export type NotificationView = components['schemas']['NotificationView']
export type StageView = components['schemas']['StageView']
export type DecisionView = components['schemas']['DecisionView']

/* --- the one next action (docs/19 A3) and the case file page. Generated. */
export type NextActionView = components['schemas']['NextActionView']
export type OfferView = components['schemas']['OfferView']
export type ChoiceView = components['schemas']['ChoiceView']
export type PenaltyOptionView = components['schemas']['PenaltyOptionView']
export type PersonView = components['schemas']['PersonView']
export type MissingView = components['schemas']['MissingView']
export type PaperOnFileView = components['schemas']['PaperOnFileView']
export type RecordPaperKind = components['schemas']['RecordPaperKind']
export type ChargeView = components['schemas']['ChargeView']
export type NoteView = components['schemas']['NoteView']
export type RecordedDecisionView = components['schemas']['RecordedDecisionView']
export type AllegationView = components['schemas']['AllegationView']
export type RegisterPersonView = components['schemas']['RegisterPersonView']
export type IngestionJobSummary = components['schemas']['IngestionJobSummary']
export type IngestionJobDetail = components['schemas']['IngestionJobDetail']
export type IngestionReceipt = components['schemas']['IngestionReceipt']
export type SegmentView = components['schemas']['SegmentView']
export type AccountingView = components['schemas']['AccountingView']

/* --- papers: the per-file intake (screen 5). Generated, like everything. */
export type SourceFileView = components['schemas']['SourceFileView']
export type SourceFileCheckRow = components['schemas']['SourceFileCheckRow']
export type SourceFileReceipt = components['schemas']['SourceFileReceipt']
export type PapersView = components['schemas']['PapersView']
export type IntakeSummaryView = components['schemas']['IntakeSummaryView']
export type IntakeEnglishView = components['schemas']['IntakeEnglishView']
export type OpenIntakeView = components['schemas']['OpenIntakeView']
export type OpenProceedingView = components['schemas']['OpenProceedingView']
export type IntakeSummaryReceipt = components['schemas']['IntakeSummaryReceipt']
export type IntakeStartReceipt = components['schemas']['IntakeStartReceipt']
export type IntakeConfirmReceipt = components['schemas']['IntakeConfirmReceipt']
export type IntakeExtraction = components['schemas']['IntakeExtraction']
export type StartIntakeBody = Body<'/api/cases/intake-start', 'post'>
export type ConfirmIntakeBody = Body<'/api/cases/{case_id}/intake-summary/confirm', 'post'>
export type AbandonIntakeBody = Body<'/api/cases/{case_id}/intake/abandon', 'post'>
export type TransliterationPreview = components['schemas']['TransliterationPreview']
export type OcrToolJobView = components['schemas']['OcrToolJobView']
export type OcrToolWarning = components['schemas']['OcrToolWarning']
export type SourceFileTextView = components['schemas']['SourceFileTextView']

/* --- Phase A intake: the confirm screen built from the one reading. Generated. */
export type IntakeView = components['schemas']['IntakeView']
export type IntakeParticularView = components['schemas']['IntakeParticularView']
export type IntakeValueView = components['schemas']['IntakeValueView']
export type IntakeHeadlineView = components['schemas']['IntakeHeadlineView']
export type IntakePaperView = components['schemas']['IntakePaperView']
export type IntakeLookView = components['schemas']['IntakeLookView']
export type IntakeActView = components['schemas']['IntakeActView']
export type IntakeAnswerView = components['schemas']['IntakeAnswerView']
export type IntakeSourceView = components['schemas']['IntakeSourceView']
export type TicketHolderView = components['schemas']['TicketHolderView']
export type CaseIdentifierView = components['schemas']['CaseIdentifierView']
export type CaseConfirmReceipt = components['schemas']['CaseConfirmReceipt']
export type AskAgainReceipt = components['schemas']['AskAgainReceipt']
export type ConfirmCaseBody = Body<'/api/cases/{case_id}/intake/confirm', 'post'>
export type AskAgainBody = Body<'/api/cases/{case_id}/intake/ask-again', 'post'>
export type IntakeValueIn = components['schemas']['IntakeValueIn']
export type IntakeActIn = components['schemas']['IntakeActIn']

/* --- the case record: what each paper says, read once (docs/19 A6). */
export type CaseRecordView = components['schemas']['CaseRecordView']
export type PaperRecordView = components['schemas']['PaperRecordView']
export type RecordEntryView = components['schemas']['RecordEntryView']
export type RecordItemView = components['schemas']['RecordItemView']
export type SetAsideView = components['schemas']['SetAsideView']
export type DiscardedView = components['schemas']['DiscardedView']
export type ReadingView = components['schemas']['ReadingView']
export type PaperKindOption = components['schemas']['PaperKindOption']
export type PaperKindReceipt = components['schemas']['PaperKindReceipt']
export type ReadingReceipt = components['schemas']['ReadingReceipt']
export type RecordItemReceipt = components['schemas']['RecordItemReceipt']
export type PaperReadingDetail = components['schemas']['PaperReadingDetail']
export type RereadPaperBody = components['schemas']['RereadPaperRequest']
export type OaMapView = components['schemas']['OaMapView']
export type SourceParagraph = components['schemas']['SourceParagraph']
export type OfficerFactRequest = components['schemas']['OfficerFactRequest']
export type OfficerFactView = components['schemas']['OfficerFactView']
export type EvidenceView = components['schemas']['EvidenceView']
export type EvidencePacket = components['schemas']['EvidencePacket']
export type EvidenceActionRequest = components['schemas']['EvidenceActionRequest']
export type EvidenceGap = components['schemas']['EvidenceGap']
export type ReplyReviewView = components['schemas']['ReplyReviewView']
export type GenerationUsageView = components['schemas']['GenerationUsageView']
export type ReplyQuestionsView = components['schemas']['ReplyQuestionsView']
export type ReplyQuestionView = components['schemas']['ReplyQuestionView']
export type AnswerQuestionRequest = components['schemas']['AnswerQuestionRequest']
export type ReviseRepliesView = components['schemas']['ReviseRepliesView']

/* --- Stage 4 on Sarvam: the court replies drafted in the background. Progress,
   the papers sent, what was left out and what it cost all ride on the version
   the workbench already polls (`drafting_usage.court`). Generated, like the rest. */
export type CourtDraftView = components['schemas']['CourtDraftView']
export type CourtPaperView = components['schemas']['CourtPaperView']
export type CourtLeftOutView = components['schemas']['CourtLeftOutView']
export type CourtNotSentView = components['schemas']['CourtNotSentView']
export type TranslatedPassageView = components['schemas']['TranslatedPassageView']
export type ModelCallView = components['schemas']['ModelCallView']
/** What a check by code found in one reply, with its level: an error or a note. */
export type BlockFindingView = components['schemas']['BlockFindingView']

/* --- Stage 4: the Brief to Counsel, and which copy of a court document stands
   as the department's (signed in the app, or the final version uploaded). */
export type CounselBriefView = components['schemas']['CounselBriefView']
export type BriefFactView = components['schemas']['BriefFactView']
export type BriefPointView = components['schemas']['BriefPointView']
export type BriefGroundView = components['schemas']['BriefGroundView']
export type BriefTranslatedView = components['schemas']['BriefTranslatedView']
export type BriefQuestionView = components['schemas']['BriefQuestionView']
export type BriefFindingView = components['schemas']['BriefFindingView']
export type BriefCallView = components['schemas']['BriefCallView']
export type FinalisedView = components['schemas']['FinalisedView']
export type CourtCaseView = components['schemas']['CourtCaseView']
export type CourtParticularsView = components['schemas']['CourtParticularsView']
export type CourtDocumentRow = components['schemas']['CourtDocumentRow']
export type FinalCopyView = components['schemas']['FinalCopyView']

/**
 * The four decisions an officer can record about a run of pages, taken off the
 * generated schema. Not-relevant and unreadable are *decisions*, not absences —
 * a page excluded on purpose and a page nobody looked at are different facts,
 * and the accounting gate treats them differently.
 */
export type Disposition = NonNullable<AssignSegmentBody['disposition']>
export type EmployeeRef = components['schemas']['EmployeeRef']
export type DocumentVersionView = components['schemas']['DocumentVersionView']
export type SignedCopyReceipt = components['schemas']['SignedCopyReceipt']
export type PleadingDraftView = components['schemas']['PleadingDraftView']
export type RepliesView = components['schemas']['RepliesView']
export type GroundsView = components['schemas']['GroundsView']
export type GroundView = components['schemas']['GroundView']
export type HoldingView = components['schemas']['HoldingView']
export type ReplyView = components['schemas']['ReplyView']
export type SourcePaperChoice = components['schemas']['SourcePaperChoice']
export type AnswerGapView = components['schemas']['AnswerGapView']
export type MarkView = components['schemas']['MarkView']
export type MarksView = components['schemas']['MarksView']
export type DocumentVersionSummary = components['schemas']['DocumentVersionSummary']
export type BlockView = components['schemas']['BlockView']
export type CheckView = components['schemas']['CheckView']
export type UnresolvedSlotView = components['schemas']['UnresolvedSlotView']
export type RewriteProposalView = components['schemas']['RewriteProposalView']
export type EnglishView = components['schemas']['EnglishView']
export type TranslatedBlockView = components['schemas']['TranslatedBlockView']

/* --- Phase A, lane D: what a draft is written from and the file lacks (A11). */
export type DraftingNeedsView = components['schemas']['DraftingNeedsView']
export type DraftingNeedView = components['schemas']['DraftingNeedView']

/**
 * `pending → drafting → drafted | blank` for a `[C]` block; `template` for
 * everything a model never touches. Taken off the schema so a screen cannot
 * invent a seventh state, and **monotonic** — a block that has landed never
 * reads `pending` again on a later poll.
 */
export type DraftState = BlockView['draft_state']

/* --- the admin editors (P10). Every one of these is generated, like the rest. */
export type TemplateRow = components['schemas']['TemplateRow']
export type TemplateDetail = components['schemas']['TemplateDetail']
export type TemplateBlockView = components['schemas']['TemplateBlockView']
export type ParagraphKindView = components['schemas']['ParagraphKind']
export type ParagraphStyleView = components['schemas']['ParagraphStyle']
export type TemplateCheckView = components['schemas']['TemplateCheckView']
export type TemplateProblemView = components['schemas']['TemplateProblemView']
export type TemplateDiff = components['schemas']['TemplateDiff']
export type SlotView = components['schemas']['SlotView']
export type OrgBlockView = components['schemas']['OrgBlockView']
export type FactorySettings = components['schemas']['FactorySettings']
export type ReloadOutcomeView = components['schemas']['ReloadOutcomeView']
export type NamedCheckView = components['schemas']['NamedCheckView']
export type ConditionView = components['schemas']['ConditionView']
export type ConditionVocabulary = components['schemas']['ConditionVocabulary']
export type ProcessDetail = components['schemas']['ProcessDetail']
export type ProcessVersionRow = components['schemas']['ProcessVersionRow']
export type ProcessDocumentView = components['schemas']['ProcessDocumentView']
export type ProcessClockView = components['schemas']['ProcessClockView']
export type ProcessGuardView = components['schemas']['ProcessGuardView']
export type ProcessDiff = components['schemas']['ProcessDiff']
export type DefinitionChangeView = components['schemas']['DefinitionChangeView']
export type MigrationPreview = components['schemas']['MigrationPreview']

/**
 * The three provenance actions, taken off the generated schema rather than
 * written out. `[S/O]` may be confirmed or reclassified and `[D]` may be
 * adopted; a local copy of that union would let a screen offer a fourth.
 */
export type ProvenanceAction = ProvenanceBody['action']

/**
 * `deemed_deputation | absorbed`, taken off the generated schema rather than
 * written out. The two populations are governed by different rules with
 * different forums, and a local copy of the union that drifted from the server
 * would let a screen offer a third.
 */
export type ServiceStatus = EmployeeRef['service_status']

/**
 * The API lives under `/api`; the screens own the root.
 *
 * `/cases` is a screen and `/api/cases` is an endpoint, and that separation is a
 * prefix on the server rather than a rule in a reverse proxy — one that a second
 * client would have to know about and could forget. This constant is the client
 * half of it, and `app/main.py`'s `API_PREFIX` is the other. Nothing else in
 * `web/` writes an API path.
 */
const BASE = '/api'

const CSRF_COOKIE = 'aweil_csrf'
const CSRF_HEADER = 'X-CSRF-Token'

/**
 * Thrown for any non-2xx. Carries the server's envelope, so a screen can render
 * the rule and the missing list rather than a status code.
 */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.error)
    this.name = 'ApiFailure'
  }

  /** What the officer has to go and supply. */
  get missing(): string[] {
    return (this.body.missing ?? []).map((m) => m.slot)
  }
}

function csrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

export interface RequestOptions {
  /** Set on any command that must not be applied twice if the network drops. */
  idempotencyKey?: string
  /** Optimistic concurrency on document and version endpoints. */
  ifMatch?: string
  signal?: AbortSignal
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...asOfHeaders() }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  // The session cookie is httpOnly, so no script can read it — including this
  // one. The readable CSRF cookie echoed in a header is the half an attacker's
  // page cannot reproduce.
  if (method !== 'GET' && method !== 'HEAD') {
    const token = csrfToken()
    if (token) headers[CSRF_HEADER] = token
  }
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
  if (options.ifMatch) headers['If-Match'] = options.ifMatch

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: options.signal,
  })

  if (!response.ok) {
    let payload: ApiError
    try {
      payload = (await response.json()) as ApiError
    } catch {
      payload = { error: response.statusText || 'The request failed.', missing: [] }
    }
    throw new ApiFailure(response.status, payload)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * The request body of an operation, straight from the generated schema.
 *
 * The keys below are the **document's** paths, which are absolute and carry the
 * `/api` prefix; the runtime paths passed to `request()` are relative to `BASE`.
 * They are written out rather than derived because a conditional type that
 * prefixed them would be cleverness in the one file whose job is to be obvious —
 * and `tsc` checks these against the generated document either way, which is how
 * the prefix move was caught rather than shipped.
 */
type Body<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
  requestBody: { content: { 'application/json': infer B } }
}
  ? B
  : never

export type CreateCaseBody = Body<'/api/cases', 'post'>
export type AskBody = Body<'/api/cases/{case_id}/chat', 'post'>
export type RecordDecisionBody = Body<
  '/api/cases/{case_id}/transitions/record-decision',
  'post'
>
export type RecordServiceBody = Body<'/api/cases/{case_id}/transitions/record-service', 'post'>
export type RecordExternalDocumentBody = Body<
  '/api/cases/{case_id}/transitions/record-external-document',
  'post'
>
export type ExtensionBody = Body<'/api/cases/{case_id}/clocks/{clock_key}/extensions', 'post'>
export type SkipDocumentBody = Body<'/api/cases/{case_id}/skip-document', 'post'>
export type MigrateBody = Body<'/api/cases/{case_id}/migrate-process-version', 'post'>
export type GenerateBody = Body<
  '/api/cases/{case_id}/documents/{document_type}/generate',
  'post'
>
export type PleadingDraftBody = Body<
  '/api/cases/{case_id}/documents/{document_type}/pleading',
  'post'
>
export type GroundsBody = Body<'/api/cases/{case_id}/documents/{document_type}/grounds', 'post'>
export type RepliesBody = Body<'/api/cases/{case_id}/documents/{document_type}/replies', 'post'>
export type RewriteProposalBody = Body<
  '/api/cases/{case_id}/documents/versions/{version_id}/rewrite-proposals',
  'post'
>
export type MarkIssuedBody = Body<'/api/cases/{case_id}/transitions/mark-issued', 'post'>
export type EditBlockBody = Body<
  '/api/cases/{case_id}/documents/versions/{version_id}/edit-block',
  'post'
>
export type FillBlanksBody = Body<
  '/api/cases/{case_id}/documents/versions/{version_id}/fill',
  'post'
>

export type BlockEditBody = Body<'/api/templates/{document_type}/draft/block', 'post'>
export type BlockAddBody = Body<'/api/templates/{document_type}/draft/block/add', 'post'>
export type DocumentContextBody = Body<'/api/templates/{document_type}/draft/context', 'post'>
export type PolishBody = Body<'/api/templates/polish', 'post'>
export type PolishProposal = components['schemas']['PolishProposal']
export type DocumentContext = components['schemas']['DocumentContext']
export type DocumentPreview = components['schemas']['DocumentPreview']
export type AdhocDocumentRow = components['schemas']['AdhocDocumentRow']
export type LibraryEntryView = components['schemas']['LibraryEntryView']
export type ComparisonView = components['schemas']['ComparisonView']
export type DifferenceView = components['schemas']['DifferenceView']
export type SharedTextView = components['schemas']['SharedTextView']
export type BlockPreviewBody = Body<
  '/api/templates/{document_type}/draft/block/preview',
  'post'
>
export type BlockPreview = components['schemas']['BlockPreview']
export type ProvenanceBody = Body<
  '/api/templates/{document_type}/draft/block/provenance',
  'post'
>
export type SlotBody = Body<'/api/templates/{document_type}/draft/slot', 'post'>
export type CheckBody = Body<'/api/templates/{document_type}/draft/check', 'post'>
export type PublishBody = Body<'/api/templates/{document_type}/draft/publish', 'post'>
export type OrgBlockEditBody = Body<'/api/org-blocks/{key}/draft/edit', 'post'>
export type StageEditBody = Body<'/api/process/draft/stage', 'post'>
export type DocumentEditBody = Body<'/api/process/draft/document', 'post'>
export type ClockEditBody = Body<'/api/process/draft/clock', 'post'>
export type GuardEditBody = Body<'/api/process/draft/guard', 'post'>

export type AssignSegmentBody = Body<
  '/api/cases/{case_id}/ingestion-jobs/{job_id}/segments/{segment_id}/assign',
  'post'
>
export type SplitSegmentBody = Body<
  '/api/cases/{case_id}/ingestion-jobs/{job_id}/segments/{segment_id}/split',
  'post'
>
export type MergeSegmentsBody = Body<
  '/api/cases/{case_id}/ingestion-jobs/{job_id}/segments/{segment_id}/merge',
  'post'
>
export type ConfirmBundleBody = Body<
  '/api/cases/{case_id}/ingestion-jobs/{job_id}/confirm',
  'post'
>
export type LabelSourceFileBody = Body<
  '/api/cases/{case_id}/source-files/{file_id}/label',
  'post'
>
export type RemoveSourceFileBody = Body<
  '/api/cases/{case_id}/source-files/{file_id}/remove',
  'post'
>
export type SetPaperKindBody = Body<'/api/cases/{case_id}/source-files/{file_id}/kind', 'post'>
export type CorrectRecordItemBody = Body<
  '/api/cases/{case_id}/record/items/{item_id}/correct',
  'post'
>
export type ConfirmRecordItemBody = Body<
  '/api/cases/{case_id}/record/items/{item_id}/confirm',
  'post'
>
export type WithdrawRecordItemBody = Body<
  '/api/cases/{case_id}/record/items/{item_id}/withdraw',
  'post'
>

export type RecordReplyBody = Body<'/api/cases/{case_id}/transitions/record-reply', 'post'>
export type RecordAppointmentBody = Body<
  '/api/cases/{case_id}/transitions/record-appointment',
  'post'
>
export type RecordApprovalBody = Body<
  '/api/cases/{case_id}/transitions/record-approval',
  'post'
>
export type EnterStageBody = Body<'/api/cases/{case_id}/transitions/enter-stage', 'post'>
export type RecordFactBody = Body<'/api/cases/{case_id}/transitions/record-fact', 'post'>
export type RecordAnswersBody = Body<'/api/cases/{case_id}/transitions/record-answers', 'post'>
export type RecordNoteBody = Body<'/api/cases/{case_id}/transitions/record-note', 'post'>
export type WithdrawCaseBody = Body<'/api/cases/{case_id}/transitions/withdraw-case', 'post'>

export type AuditPage = components['schemas']['AuditPage']
export type AuditEntryView = components['schemas']['AuditEntryView']

export const api = {
  login: (username: string, password: string) =>
    request<UserView>('POST', '/auth/login', { username, password }),

  logout: () => request<{ signed_out: boolean }>('POST', '/auth/logout'),

  me: () => request<UserView>('GET', '/auth/me'),
  /** Liveness, the server's today, and whether a test setup's "working as of"
   *  control is on. */
  health: () =>
    request<paths['/api/health']['get']['responses']['200']['content']['application/json']>(
      'GET',
      '/health',
    ),

  listCases: () => request<CaseSummary[]>('GET', '/cases'),

  getCase: (caseId: string) => request<CaseDetail>('GET', `/cases/${caseId}`),

  /**
   * **The only truth about what is possible now.** Never compute availability
   * on the client — a screen that decides for itself is a second state machine,
   * and the two disagree the moment a rule changes.
   */
  getTransitions: (caseId: string) =>
    request<TransitionEnvelope>('GET', `/cases/${caseId}/transitions`),

  createCase: (body: CreateCaseBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', '/cases', body, o),

  recordDecision: (caseId: string, body: RecordDecisionBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/record-decision`, body, o),

  recordService: (caseId: string, body: RecordServiceBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/record-service`, body, o),

  recordExternalDocument: (
    caseId: string,
    body: RecordExternalDocumentBody,
    o?: RequestOptions,
  ) =>
    request<TransitionReceipt>(
      'POST',
      `/cases/${caseId}/transitions/record-external-document`,
      body,
      o,
    ),

  grantExtension: (caseId: string, clockKey: string, body: ExtensionBody, o?: RequestOptions) =>
    request<TransitionReceipt>(
      'POST',
      `/cases/${caseId}/clocks/${clockKey}/extensions`,
      body,
      o,
    ),

  skipDocument: (caseId: string, body: SkipDocumentBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/skip-document`, body, o),

  withdrawDocument: (caseId: string, body: SkipDocumentBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/withdraw-document`, body, o),

  migrateProcessVersion: (caseId: string, body: MigrateBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/migrate-process-version`, body, o),

  /** The Charged Officer answered. This closes a statutory period. */
  recordReply: (caseId: string, body: RecordReplyBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/record-reply`, body, o),

  /** Who was appointed to a role — a person, not only a document. */
  recordAppointment: (caseId: string, body: RecordAppointmentBody, o?: RequestOptions) =>
    request<TransitionReceipt>(
      'POST',
      `/cases/${caseId}/transitions/record-appointment`,
      body,
      o,
    ),

  recordApproval: (caseId: string, body: RecordApprovalBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/record-approval`, body, o),

  enterStage: (caseId: string, body: EnterStageBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/enter-stage`, body, o),

  recordFact: (caseId: string, body: RecordFactBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/record-fact`, body, o),

  /** His answer to each charge — recorded as his response the rules read. */
  recordAnswers: (caseId: string, body: RecordAnswersBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/record-answers`, body, o),

  /** One line, for something said and never written down. */
  recordNote: (caseId: string, body: RecordNoteBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/record-note`, body, o),

  /** Withdrawal is a state: the case keeps every row, and the reason is on the record. */
  withdrawCase: (caseId: string, body: WithdrawCaseBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/withdraw-case`, body, o),

  /** The factory's register, less the Charged Officer — who a case may name. */
  listCasePeople: (caseId: string) =>
    request<RegisterPersonView[]>('GET', `/cases/${caseId}/people`),

  /* ----------------------------------------------------------------- audit */

  /**
   * The chain, human-readable. **Every page carries `limitation`** — the honest
   * statement of what hash-linking cannot detect — and it is rendered verbatim
   * rather than summarised, for the same reason an unverified statutory marker
   * may not be removed to tidy a screen.
   */
  listAudit: (limit = 100, before?: number) =>
    request<AuditPage>('GET', `/audit?limit=${limit}${before ? `&before=${before}` : ''}`),

  listCaseAudit: (caseId: string, limit = 200) =>
    request<AuditPage>('GET', `/cases/${caseId}/audit?limit=${limit}`),

  /** The URL the browser downloads from. Not fetched here — the browser does it. */
  auditCsvUrl: (caseId?: string) =>
    `/api${caseId ? `/audit.csv?case_id=${caseId}` : '/audit.csv'}`,

  /* ------------------------------------------------------------- documents */

  /**
   * Assemble a document from its pinned template.
   *
   * Returns as soon as the deterministic half is written. With inference on the
   * `[C]` paragraphs come back `pending` and a worker fills them, so the answer
   * to this is a page to read rather than a wait to sit through — poll
   * `readVersion` while `drafting` is true.
   */
  generateDocument: (
    caseId: string,
    documentType: string,
    body: GenerateBody,
    o?: RequestOptions,
  ) =>
    request<DocumentVersionView>(
      'POST',
      `/cases/${caseId}/documents/${documentType}/generate`,
      body,
      o,
    ),

  /**
   * The papers on this file, to choose the pleading being answered.
   *
   * Unfiltered on purpose: the officer knows which paper is the Original
   * Application and the server does not, so a filtered list would make a
   * correctly-filed paper unchoosable with nothing on screen saying why.
   */
  pleadingSourcePapers: (caseId: string, documentType: string) =>
    request<SourcePaperChoice[]>(
      'GET',
      `/cases/${caseId}/documents/${documentType}/pleading/papers`,
    ),

  /**
   * Draft a tribunal or court pleading against a paper on the file.
   *
   * Separate from `generateDocument` because it has a second source: the
   * application served on the department, read as pages rather than as a
   * transcription, so the reply's paragraph numbering corresponds to the
   * application's own.
   *
   * **No polling.** The model call happens inside this request, so what comes
   * back is the finished document — not a page with paragraphs still owed.
   */
  draftPleading: (
    caseId: string,
    documentType: string,
    body: PleadingDraftBody,
    o?: RequestOptions,
  ) =>
    request<PleadingDraftView>(
      'POST',
      `/cases/${caseId}/documents/${documentType}/pleading`,
      body,
      o,
    ),

  /**
   * Draft the paragraph-wise reply to a pleading on the file.
   *
   * **Writes no document.** The office has its counter affidavit in Word; what
   * it cannot do quickly is answer thirty-eight paragraphs of a sixty-page
   * application, and that is all this returns.
   *
   * **Two ways, and the answer says which.** Stage 4 on Sarvam returns at once
   * with `document.drafting` true and every reply pending: a background job
   * writes them batch by batch, and the page polls `readVersion`, where
   * progress rides on `drafting_usage.court`. The earlier way (still the
   * fallback, chosen by a server setting) runs the batches inside the request
   * and returns the finished replies, with no `court` object at all.
   */
  /**
   * Draft the grounds of the department's own writ petition.
   *
   * Separate from `draftReplies` because it is the other posture: that answers
   * somebody else's paragraphs, this attacks an order. What comes back is `[D]`
   * — our unsourced draft, flagged wherever it is shown.
   */
  draftGrounds: (caseId: string, documentType: string, body: GroundsBody, o?: RequestOptions) =>
    request<GroundsView>('POST', `/cases/${caseId}/documents/${documentType}/grounds`, body, o),

  draftReplies: (caseId: string, documentType: string, body: RepliesBody, o?: RequestOptions) =>
    request<RepliesView>('POST', `/cases/${caseId}/documents/${documentType}/replies`, body, o),

  /**
   * What a court reply would be drafted from, before anything is drafted: the
   * papers found, what is expected and not uploaded, the size against the limit
   * and an estimated cost (`usage.court`). The same command as `draftReplies`
   * with `preflight` set — it touches no model and writes nothing. On a
   * deployment still drafting the earlier way it answers with no `usage`.
   */
  previewReplies: (
    caseId: string,
    documentType: string,
    sourceFileId: RepliesBody['source_file_id'],
  ) =>
    request<RepliesView>('POST', `/cases/${caseId}/documents/${documentType}/replies`, {
      preflight: true,
      source_file_id: sourceFileId,
    } satisfies Partial<RepliesBody>),

  /**
   * Draft again against the same pleading. Where an earlier draft stopped
   * half-way and the papers have not changed, the server carries the replies
   * already written into the new version and pays only for the rest — that
   * rule is the server's, so nothing here says which paragraphs to keep.
   */
  continueReplies: (
    caseId: string,
    documentType: string,
    sourceFileId: RepliesBody['source_file_id'],
    /** Read the pleading's paragraphs afresh, for a reading that missed one.
     *  Nothing is carried from the earlier version then; the server says so. */
    readParagraphsAgain = false,
  ) =>
    request<RepliesView>('POST', `/cases/${caseId}/documents/${documentType}/replies`, {
      source_file_id: sourceFileId,
      ...(readParagraphsAgain ? { read_paragraphs_again: true } : {}),
    } satisfies Partial<RepliesBody>),

  /**
   * The Brief to Counsel for a court document: ask for it, and read it.
   *
   * Asking returns at once — a background job writes the four sections one
   * after another from the same papers as the replies. Poll `readCounselBrief`
   * while `state` is `queued` or `drafting`. Refused with the reason (409)
   * where there are no replies yet or they are still being drafted, and (503)
   * where no drafting service is configured.
   */
  draftCounselBrief: (caseId: string, documentType: string) =>
    request<CounselBriefView>(
      'POST',
      `/cases/${caseId}/documents/${documentType}/counsel-brief`,
    ),
  /** No model call, ever. `state` is `none` where no Brief has been asked for. */
  readCounselBrief: (caseId: string, documentType: string) =>
    request<CounselBriefView>(
      'GET',
      `/cases/${caseId}/documents/${documentType}/counsel-brief`,
    ),
  /** The Brief as a file. The browser fetches this URL, not `request()`. */
  counselBriefPath: (caseId: string, documentType: string, format: 'docx' | 'pdf') =>
    `${BASE}/cases/${caseId}/documents/${documentType}/counsel-brief/download?format=${format}`,

  /**
   * Which copy of a court document stands as the department's: the signing
   * recorded in the app (who, when), every final version uploaded since, and
   * which of them the next stage is drafted from — with the server's own
   * sentence for it (`current_says`).
   */
  readFinalised: (caseId: string, documentType: string) =>
    request<FinalisedView>('GET', `/cases/${caseId}/documents/${documentType}/finalised`),

  /**
   * The court replies of a case (4.1, 4.2, 4.3) for the case page: whether the
   * pleading each one answers is on the file, and how far its draft has got.
   * Read-only; the server names every document off the case's own process version.
   */
  readCourtDocuments: (caseId: string) =>
    request<CourtCaseView>('GET', `/cases/${caseId}/court-documents`),

  /**
   * His name, ticket number, post and section as the papers of a court case
   * print them: one narrow reading, each value checked by the server against
   * the page it stands on. An offer for the Confirm screen — it writes nothing.
   * A POST because the first asking is a paid call; asked again it is served
   * from what was kept.
   */
  readCourtParticulars: (caseId: string) =>
    request<CourtParticularsView>('POST', `/cases/${caseId}/court-particulars`, {}),

  getOaMap: (caseId: string, fileId: string) =>
    request<OaMapView | null>(
      'GET',
      `/cases/${caseId}/documents/D18/paragraph-map?source_file_id=${fileId}`,
    ),
  prepareOaMap: (caseId: string, fileId: string) =>
    request<OaMapView | null>('POST', `/cases/${caseId}/documents/D18/paragraph-map/prepare`, {
      source_file_id: fileId,
    }),
  confirmOaMap: (
    caseId: string,
    mapId: string,
    body: components['schemas']['ConfirmOaRequest'],
  ) =>
    request<OaMapView | null>(
      'POST',
      `/cases/${caseId}/documents/D18/paragraph-maps/${mapId}/confirm`,
      body,
    ),
  listOfficerFacts: (caseId: string) =>
    request<OfficerFactView[]>('GET', `/cases/${caseId}/documents/D18/facts`),
  recordOfficerFact: (caseId: string, body: OfficerFactRequest) =>
    request<OfficerFactView[]>('POST', `/cases/${caseId}/documents/D18/facts`, body),
  commentsPath: (caseId: string, versionId: string) =>
    `${BASE}/cases/${caseId}/documents/versions/${versionId}/comments`,
  commentsPdfPath: (caseId: string, versionId: string) =>
    `${BASE}/cases/${caseId}/documents/versions/${versionId}/comments/pdf`,
  /** The Need your input list, read off the saved replies. No model call. */
  getReplyQuestions: (caseId: string, versionId: string) =>
    request<ReplyQuestionsView>(
      'GET',
      `/cases/${caseId}/documents/versions/${versionId}/questions`,
    ),
  answerReplyQuestion: (caseId: string, versionId: string, body: AnswerQuestionRequest) =>
    request<ReplyQuestionsView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/questions/answer`,
      body,
    ),
  /** One metered call for the affected paragraphs; results are Accept/Reject proposals. */
  reviseReplies: (caseId: string, versionId: string, paragraphIds: string[]) =>
    request<ReviseRepliesView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/replies/revise`,
      { paragraph_ids: paragraphIds },
    ),
  getReplyEvidence: (caseId: string, fileId: string) =>
    request<EvidenceView>(
      'GET',
      `/cases/${caseId}/documents/D18/evidence?source_file_id=${fileId}`,
    ),
  prepareReplyEvidence: (caseId: string, fileId: string, retry = false) =>
    request<EvidenceView>('POST', `/cases/${caseId}/documents/D18/evidence/prepare`, {
      source_file_id: fileId,
      retry,
    }),
  getVersionEvidence: (caseId: string, versionId: string) =>
    request<EvidenceView>('GET', `/cases/${caseId}/documents/versions/${versionId}/evidence`),
  recordEvidenceAction: (caseId: string, body: EvidenceActionRequest) =>
    request<EvidenceView>('POST', `/cases/${caseId}/documents/D18/evidence/actions`, body),
  checkReplyEvidence: (caseId: string, versionId: string) =>
    request<EvidenceView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/evidence/check`,
      {},
    ),
  proposeEvidenceUpdate: (caseId: string, versionId: string, paragraphId: string) =>
    request<EvidenceView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/evidence/propose`,
      { paragraph_id: paragraphId },
    ),
  getReplyReview: (caseId: string, versionId: string) =>
    request<ReplyReviewView>(
      'GET',
      `/cases/${caseId}/documents/versions/${versionId}/reply-review`,
    ),
  checkReplyCorrespondence: (caseId: string, versionId: string) =>
    request<ReplyReviewView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/reply-review/check`,
      {},
    ),
  recordReplyReview: (
    caseId: string,
    versionId: string,
    body: components['schemas']['ReviewRepliesRequest'],
  ) =>
    request<ReplyReviewView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/reply-review/record`,
      body,
    ),

  /** The page as it stands right now. No model call — this reads a row. */
  readVersion: (caseId: string, versionId: string) =>
    request<DocumentVersionView>('GET', `/cases/${caseId}/documents/versions/${versionId}`),

  // ADR-025. Records that a person looked at one mark — not that the value is
  // right. Idempotent, and it returns the whole set so the countdown the
  // officer is reading comes back from the server rather than being kept here.
  checkDocumentMark: (caseId: string, versionId: string, markKey: string) =>
    request<MarksView>('POST', `/cases/${caseId}/documents/versions/${versionId}/marks/check`, {
      preflight: false,
      mark_key: markKey,
    }),

  /**
   * What this draft is written from that the file does not have — asked before
   * drafting (A11): upload it, record it on the case page, or draft anyway.
   * Reads only; no provider is touched.
   */
  draftingNeeds: (
    caseId: string,
    documentType: string,
    choice: { purpose?: string | null; part?: string | null } = {},
  ) => {
    const query = new URLSearchParams()
    if (choice.purpose) query.set('purpose', choice.purpose)
    if (choice.part) query.set('part', choice.part)
    const tail = query.toString()
    return request<DraftingNeedsView>(
      'GET',
      `/cases/${caseId}/documents/${documentType}/drafting-needs${tail ? `?${tail}` : ''}`,
    )
  },

  /** Every version, newest first. Nothing is ever deleted. */
  // No occurrence asks the server for the current one. Naming 1 here is how
  // the workbench came to show the filed D18 and not the one being drafted.
  listVersions: (caseId: string, documentType: string, occurrence?: number) =>
    request<DocumentVersionSummary[]>(
      'GET',
      `/cases/${caseId}/documents/${documentType}/versions${
        occurrence === undefined ? '' : `?occurrence=${occurrence}`
      }`,
    ),

  preIssueChecks: (caseId: string, versionId: string) =>
    request<CheckView[]>('GET', `/cases/${caseId}/documents/versions/${versionId}/pre-issue`),

  /**
   * The officer's own values for the blanks. A new version, never an edit.
   *
   * Not the diff gate — rule 2 is about the machine's changes, and these words
   * are the officer's. `If-Match` is still required: a value typed against a
   * document that has since moved would be written into text nobody read.
   */
  fillBlanks: (caseId: string, versionId: string, body: FillBlanksBody, ifMatch: string) =>
    request<DocumentVersionView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/fill`,
      body,
      { ifMatch },
    ),

  /**
   * The officer's own wording for one block. A new version, never an edit in
   * place — and rule 4 travels: what the block said before stays on it.
   */
  editBlock: (caseId: string, versionId: string, body: EditBlockBody, ifMatch: string) =>
    request<DocumentVersionView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/edit-block`,
      body,
      { ifMatch },
    ),

  /** The officer's exact D18 wording, stored for Accept or Reject before it changes the draft. */
  proposeManualEdit: (
    caseId: string,
    versionId: string,
    body: EditBlockBody,
    ifMatch: string,
  ) =>
    request<RewriteProposalView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/manual-edit-proposals`,
      body,
      { ifMatch },
    ),

  /** A **proposal**. Nothing is written until it is accepted. */
  proposeRewrite: (caseId: string, versionId: string, body: RewriteProposalBody) =>
    request<RewriteProposalView>(
      'POST',
      `/cases/${caseId}/documents/versions/${versionId}/rewrite-proposals`,
      body,
    ),

  /** The only call that writes. Creates a new version; `If-Match` is required. */
  acceptProposal: (caseId: string, proposalId: string, ifMatch: string) =>
    request<DocumentVersionView>(
      'POST',
      `/cases/${caseId}/documents/proposals/${proposalId}/accept`,
      undefined,
      { ifMatch },
    ),

  /** Leaves no trace on the document. The proposal row stays as a ledger. */
  rejectProposal: (caseId: string, proposalId: string) =>
    request<RewriteProposalView>(
      'POST',
      `/cases/${caseId}/documents/proposals/${proposalId}/reject`,
    ),

  /** The reading. Never the issued instrument. */
  englishView: (caseId: string, versionId: string) =>
    request<EnglishView>('GET', `/cases/${caseId}/documents/versions/${versionId}/english`),

  /**
   * The DOCX path. Not fetched through `request` — the response is a file, and
   * it is streamed rather than presigned so the pre-issue gate cannot be walked
   * around. A 409 here carries the failing check in the normal envelope.
   */
  docxPath: (caseId: string, versionId: string) =>
    `${BASE}/cases/${caseId}/documents/versions/${versionId}/docx`,

  markIssued: (caseId: string, body: MarkIssuedBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/transitions/mark-issued`, body, o),

  getRule: (ref: string) => request<RuleView>('GET', `/rules/${ref}`),

  listRules: () => request<RuleView[]>('GET', '/rules'),

  getCaseChat: (caseId: string) => request<ChatPanel>('GET', `/cases/${caseId}/chat`),

  askCase: (caseId: string, body: AskBody) =>
    request<ChatAnswerView>('POST', `/cases/${caseId}/chat`, body),

  /* --- accounts: the section administers its own factory (CP-ROLE-01) ------
   *
   * Every command carries the acting officer's own password. That is not
   * belt-and-braces on top of the session — it is what makes administering an
   * account a deliberate act, and it proves who is at the keyboard rather than
   * merely that a session is open.
   */

  listAccounts: () => request<AccountView[]>('GET', '/accounts'),

  createAccount: (body: CreateAccountBody) => request<AccountView>('POST', '/accounts', body),

  setAccountPassword: (username: string, body: SetPasswordBody) =>
    request<{ password_set: boolean; sessions_ended: boolean }>(
      'POST',
      `/accounts/${encodeURIComponent(username)}/password`,
      body,
    ),

  deactivateAccount: (username: string, body: ReauthBody) =>
    request<{ closed: boolean }>(
      'POST',
      `/accounts/${encodeURIComponent(username)}/deactivate`,
      body,
    ),

  reactivateAccount: (username: string, body: ReauthBody) =>
    request<{ reopened: boolean }>(
      'POST',
      `/accounts/${encodeURIComponent(username)}/reactivate`,
      body,
    ),

  clearAccountLockout: (username: string, body: ReauthBody) =>
    request<{ counters_dropped: number | null }>(
      'POST',
      `/accounts/${encodeURIComponent(username)}/clear-lockout`,
      body,
    ),

  /** Every role may do this, the CGM included. Keeps this session alive and
   *  ends every other one. */
  changeOwnPassword: (body: ChangeOwnPasswordBody) =>
    request<{ password_changed: boolean; other_sessions_ended: boolean }>(
      'POST',
      '/accounts/me/password',
      body,
    ),

  listNotifications: () => request<NotificationView[]>('GET', '/notifications'),

  markNotificationRead: (id: string) =>
    request<NotificationView>('POST', `/notifications/${id}/read`),

  listBundles: (caseId: string) =>
    request<IngestionJobSummary[]>('GET', `/cases/${caseId}/ingestion-jobs`),

  getBundle: (caseId: string, jobId: string) =>
    request<IngestionJobDetail>('GET', `/cases/${caseId}/ingestion-jobs/${jobId}`),

  /**
   * Multipart, so it does not go through `request()` — which sets a JSON
   * content type and would have the browser send a body FastAPI cannot parse.
   * The CSRF header and the cookie behave exactly as everywhere else.
   */
  uploadBundle: async (caseId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    const token = csrfToken()
    const response = await fetch(`${BASE}/cases/${caseId}/ingestion-jobs`, {
      method: 'POST',
      headers: token
        ? { [CSRF_HEADER]: token, Accept: 'application/json' }
        : { Accept: 'application/json' },
      credentials: 'include',
      body: form,
    })
    if (!response.ok) {
      let payload: ApiError
      try {
        payload = (await response.json()) as ApiError
      } catch {
        payload = { error: response.statusText || 'The upload failed.', missing: [] }
      }
      throw new ApiFailure(response.status, payload)
    }
    return (await response.json()) as IngestionJobSummary
  },

  /* ------------------------------------------------- papers (screen 5) --- */

  listSourceFiles: (caseId: string) =>
    request<PapersView>('GET', `/cases/${caseId}/source-files`),

  /**
   * Multipart, like `uploadBundle` — and the load-bearing half of the ruled
   * requirement: naming a document type here *records* the document on the
   * case, through the one transition path. The upload is never refused for
   * what the case holds; checks report on the list and bite elsewhere.
   */
  uploadSourceFile: async (
    caseId: string,
    file: File,
    label: {
      document_type?: string
      other_description?: string
      document_date?: string
      stage_key?: string
      /** The kind the door settles — the intake's complaint box, or the
       * officer's pick from the paper kinds (Phase A). */
      paper_kind?: string
    } = {},
  ) => {
    const form = new FormData()
    form.append('file', file)
    for (const [key, value] of Object.entries(label)) {
      if (value) form.append(key, value)
    }
    const token = csrfToken()
    const response = await fetch(`${BASE}/cases/${caseId}/source-files`, {
      method: 'POST',
      headers: token
        ? { [CSRF_HEADER]: token, Accept: 'application/json', ...asOfHeaders() }
        : { Accept: 'application/json', ...asOfHeaders() },
      credentials: 'include',
      body: form,
    })
    if (!response.ok) {
      let payload: ApiError
      try {
        payload = (await response.json()) as ApiError
      } catch {
        payload = { error: response.statusText || 'The upload failed.', missing: [] }
      }
      throw new ApiFailure(response.status, payload)
    }
    return (await response.json()) as SourceFileReceipt
  },

  labelSourceFile: (
    caseId: string,
    fileId: string,
    body: LabelSourceFileBody,
    o?: RequestOptions,
  ) =>
    request<SourceFileReceipt>(
      'POST',
      `/cases/${caseId}/source-files/${fileId}/label`,
      body,
      o,
    ),

  removeSourceFile: (
    caseId: string,
    fileId: string,
    body: RemoveSourceFileBody,
    o?: RequestOptions,
  ) =>
    request<SourceFileReceipt>(
      'POST',
      `/cases/${caseId}/source-files/${fileId}/remove`,
      body,
      o,
    ),

  /** The paper's read text — the preview a .docx gets, since browsers render
   * scans but not Word files. Null text renders as "unread" in words. */
  getSourceFileText: (caseId: string, fileId: string) =>
    request<SourceFileTextView>('GET', `/cases/${caseId}/source-files/${fileId}/text`),

  /** The scan itself, streamed. The browser fetches this URL, not `request()`. */
  sourceFilePath: (caseId: string, fileId: string) =>
    `${BASE}/cases/${caseId}/source-files/${fileId}/source`,

  /* ------------------------------- the case record (docs/19 A6) --- */

  /** What the record says, paper by paper — computed on each read, with what
   * each reading threw out and what an officer withdrew, never hidden. */
  getCaseRecord: (caseId: string) => request<CaseRecordView>('GET', `/cases/${caseId}/record`),

  getPaperReading: (caseId: string, fileId: string) =>
    request<PaperReadingDetail>('GET', `/cases/${caseId}/source-files/${fileId}/reading`),

  /** Free when nothing about the paper changed: the same text read with the
   * same question is never paid for twice. */
  rereadPaper: (
    caseId: string,
    fileId: string,
    body: RereadPaperBody = { preflight: false },
    o?: RequestOptions,
  ) =>
    request<ReadingReceipt>('POST', `/cases/${caseId}/source-files/${fileId}/re-read`, body, o),

  /** *Confirm* or *Pick another*. Files nothing; reads the paper again only if
   * it was read as something else. */
  setPaperKind: (caseId: string, fileId: string, body: SetPaperKindBody, o?: RequestOptions) =>
    request<PaperKindReceipt>('POST', `/cases/${caseId}/source-files/${fileId}/kind`, body, o),

  /** Each of the three adds an entry with its reason; nothing is overwritten,
   * and the app's reading stays beneath the officer's (rule 4). */
  correctRecordItem: (
    caseId: string,
    itemId: string,
    body: CorrectRecordItemBody,
    o?: RequestOptions,
  ) =>
    request<RecordItemReceipt>(
      'POST',
      `/cases/${caseId}/record/items/${itemId}/correct`,
      body,
      o,
    ),

  confirmRecordItem: (
    caseId: string,
    itemId: string,
    body: ConfirmRecordItemBody,
    o?: RequestOptions,
  ) =>
    request<RecordItemReceipt>(
      'POST',
      `/cases/${caseId}/record/items/${itemId}/confirm`,
      body,
      o,
    ),

  withdrawRecordItem: (
    caseId: string,
    itemId: string,
    body: WithdrawRecordItemBody,
    o?: RequestOptions,
  ) =>
    request<RecordItemReceipt>(
      'POST',
      `/cases/${caseId}/record/items/${itemId}/withdraw`,
      body,
      o,
    ),

  getIntakeSummary: (caseId: string) =>
    request<IntakeSummaryView>('GET', `/cases/${caseId}/intake-summary`),

  /** Writes only the machine columns, each once. The officer's lines are
   * another screen's act; a provider outage answers 503 naming the service. */
  draftIntakeSummary: (caseId: string, o?: RequestOptions) =>
    request<IntakeSummaryReceipt>('POST', `/cases/${caseId}/intake-summary/draft`, {}, o),

  /* ------------------------------------- the fresh-complaint journey --- */

  /** Claims the serial at the start (ruling 1): the case and its placeholder
   * employee exist before the complaint's read runs. `opened_on` is the
   * organisation's today, resolved on the server — never the browser's. */
  startIntake: (body: StartIntakeBody, o?: RequestOptions) =>
    request<IntakeStartReceipt>('POST', '/cases/intake-start', body, o),

  /** The officer's one confirming act — every value theirs, stamped server-side.
   * The machine's lines stay beside them forever. */
  confirmIntake: (caseId: string, body: ConfirmIntakeBody, o?: RequestOptions) =>
    request<IntakeConfirmReceipt>('POST', `/cases/${caseId}/intake-summary/confirm`, body, o),

  /** The way out of a started intake: WITHDRAWN with a reason, never deleted —
   * the register carries no gap. */
  abandonIntake: (caseId: string, body: AbandonIntakeBody, o?: RequestOptions) =>
    request<TransitionReceipt>('POST', `/cases/${caseId}/intake/abandon`, body, o),

  /** Intakes started and never confirmed — the door offers to resume one
   * instead of claiming a fresh serial (entry 4). */
  listOpenIntakes: () => request<OpenIntakeView[]>('GET', '/intakes/open'),

  /** Reference translations of the machine's Hindi lines, for the English-only
   * confirm screen. Readings, never instruments (entry 9). */
  intakeEnglish: (caseId: string) =>
    request<IntakeEnglishView>('GET', `/cases/${caseId}/intake-english`),

  transliterationPreview: (termEn: string, unit?: string) =>
    request<TransliterationPreview>(
      'GET',
      `/transliteration/preview?term_en=${encodeURIComponent(termEn)}${
        unit ? `&unit=${encodeURIComponent(unit)}` : ''
      }`,
    ),

  /**
   * Put the pages nothing read onto the page map, so a bundle whose text could
   * not be fetched is still reviewable from the scan.
   */
  reviewBundleByHand: (caseId: string, jobId: string, o?: RequestOptions) =>
    request<IngestionJobDetail>(
      'POST',
      `/cases/${caseId}/ingestion-jobs/${jobId}/review-by-hand`,
      {},
      o,
    ),

  retryBundle: (caseId: string, jobId: string, o?: RequestOptions) =>
    request<IngestionJobSummary>(
      'POST',
      `/cases/${caseId}/ingestion-jobs/${jobId}/retry`,
      {},
      o,
    ),

  assignSegment: (
    caseId: string,
    jobId: string,
    segmentId: string,
    body: AssignSegmentBody,
    o?: RequestOptions,
  ) =>
    request<IngestionJobDetail>(
      'POST',
      `/cases/${caseId}/ingestion-jobs/${jobId}/segments/${segmentId}/assign`,
      body,
      o,
    ),

  splitSegment: (
    caseId: string,
    jobId: string,
    segmentId: string,
    body: SplitSegmentBody,
    o?: RequestOptions,
  ) =>
    request<IngestionJobDetail>(
      'POST',
      `/cases/${caseId}/ingestion-jobs/${jobId}/segments/${segmentId}/split`,
      body,
      o,
    ),

  mergeSegments: (
    caseId: string,
    jobId: string,
    segmentId: string,
    body: MergeSegmentsBody,
    o?: RequestOptions,
  ) =>
    request<IngestionJobDetail>(
      'POST',
      `/cases/${caseId}/ingestion-jobs/${jobId}/segments/${segmentId}/merge`,
      body,
      o,
    ),

  confirmBundle: (caseId: string, jobId: string, body: ConfirmBundleBody, o?: RequestOptions) =>
    request<IngestionReceipt>(
      'POST',
      `/cases/${caseId}/ingestion-jobs/${jobId}/confirm`,
      body,
      o,
    ),

  verifyAudit: (expectedHead?: string) =>
    request<AuditVerification>(
      'GET',
      `/audit/verify${expectedHead ? `?expected_head=${encodeURIComponent(expectedHead)}` : ''}`,
    ),

  /* ------------------------------------------------- the template editor --
   *
   * Every editing call addresses **the open draft**, which is why none of them
   * takes a version number: a published version is frozen and there is exactly
   * one thing an edit can reach. `openTemplateDraft` is what creates it, and it
   * is idempotent — two administrators clicking Edit get the same draft.
   */

  listTemplates: () => request<TemplateRow[]>('GET', '/templates'),

  /** One language's template. Hindi is the source; a language this document
   * has never been started in is a 404 naming the ones that exist, never a
   * silent fall back — that would show a Hindi template under an English tab. */
  getTemplate: (documentType: string, language = 'hi') =>
    request<TemplateDetail>('GET', `/templates/${documentType}?language=${language}`),

  /** Start this document in another language, seeded once from the source and
   * owned separately from then on. Never a live translation. */
  addTemplateLanguage: (documentType: string, body: { preflight: false; language: 'en' }) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/languages`, body),

  getTemplateVersion: (documentType: string, version: number) =>
    request<TemplateDetail>('GET', `/templates/${documentType}/versions/${version}`),

  getTemplateDiff: (documentType: string) =>
    request<TemplateDiff>('GET', `/templates/${documentType}/diff`),

  openTemplateDraft: (documentType: string, language = 'hi') =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft?language=${language}`),

  editTemplateBlock: (documentType: string, body: BlockEditBody) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/block`, body),

  /** Templates the nine factories have shared with each other — the one
   * surface deliberately outside the unit boundary. */
  listLibrary: () => request<LibraryEntryView[]>('GET', '/library'),

  /** Exactly what publishing this would share. A screen, not a scanner: a
   * template is office procedure only if nothing in it names a case. */
  readSharedText: (documentType: string) =>
    request<SharedTextView[]>('GET', `/templates/${documentType}/shared-text`),

  /** Share this factory's template. A snapshot, never a live link. */
  publishToLibrary: (documentType: string, body: { preflight: false; note: string }) =>
    request<LibraryEntryView>('POST', `/templates/${documentType}/publish-to-library`, body),

  /** Take a copy. From here it is ours to edit. */
  adoptFromLibrary: (body: { preflight: false; entry_id: string }) =>
    request<TemplateDetail>('POST', '/library/adopt', body),

  /** Attach the signed copy of a draft this office made. Recorded at once and
   * never refused; it becomes the version later documents read. Issuing stays
   * the officer's own act (mark-issued). */
  recordSignedCopy: async (
    caseId: string,
    documentType: string,
    versionId: string,
    file: File,
    signedOn?: string,
    /** The date it was filed before the tribunal or court (the court replies). */
    filedOn?: string,
  ) => {
    const form = new FormData()
    form.append('file', file)
    if (signedOn) form.append('signed_on', signedOn)
    if (filedOn) form.append('filed_on', filedOn)
    const token = csrfToken()
    const response = await fetch(
      `${BASE}/cases/${caseId}/documents/${documentType}/versions/${versionId}/signed-copy`,
      {
        method: 'POST',
        headers: token
          ? { [CSRF_HEADER]: token, Accept: 'application/json' }
          : { Accept: 'application/json' },
        credentials: 'include',
        body: form,
      },
    )
    if (!response.ok) {
      let payload: ApiError
      try {
        payload = (await response.json()) as ApiError
      } catch {
        payload = {
          error: response.statusText || 'The signed copy was not recorded.',
          missing: [],
        }
      }
      throw new ApiFailure(response.status, payload)
    }
    return (await response.json()) as SignedCopyReceipt
  },

  /** Judge this draft against a document the office actually issued for the
   * same case. Reports differences and what kind each is; proposes nothing. */
  compareAgainstReal: async (
    documentType: string,
    caseId: string,
    file: File,
    language = 'hi',
  ) => {
    const form = new FormData()
    form.append('case_id', caseId)
    form.append('language', language)
    form.append('file', file)
    const token = csrfToken()
    const response = await fetch(`${BASE}/templates/${documentType}/draft/compare`, {
      method: 'POST',
      headers: token
        ? { [CSRF_HEADER]: token, Accept: 'application/json' }
        : { Accept: 'application/json' },
      credentials: 'include',
      body: form,
    })
    if (!response.ok) {
      let payload: ApiError
      try {
        payload = (await response.json()) as ApiError
      } catch {
        payload = { error: response.statusText || 'The comparison failed.', missing: [] }
      }
      throw new ApiFailure(response.status, payload)
    }
    return (await response.json()) as ComparisonView
  },

  /** Read two or more real documents of this type and propose the template
   * behind them, into the open draft. Multipart — the officer's real files. */
  learnTemplate: async (documentType: string, purpose: string, files: File[]) => {
    const form = new FormData()
    form.append('purpose', purpose)
    for (const file of files) form.append('files', file)
    // No Content-Type header, exactly as `uploadBundle`: setting one drops the
    // boundary and FastAPI cannot parse the body.
    const token = csrfToken()
    const response = await fetch(`${BASE}/templates/${documentType}/draft/learn`, {
      method: 'POST',
      headers: token
        ? { [CSRF_HEADER]: token, Accept: 'application/json' }
        : { Accept: 'application/json' },
      credentials: 'include',
      body: form,
    })
    if (!response.ok) {
      let payload: ApiError
      try {
        payload = (await response.json()) as ApiError
      } catch {
        payload = { error: response.statusText || 'Reading the documents failed.', missing: [] }
      }
      throw new ApiFailure(response.status, payload)
    }
    return (await response.json()) as TemplateDetail
  },

  /** A document outside the twenty, owned by this factory. Officers write
   * correspondence every day and the product helped with none of it. */
  createAdhocTemplate: (body: { preflight: false; title: string; title_hi?: string }) =>
    request<TemplateDetail>('POST', '/templates/adhoc', body),

  /** Rename a document this office wrote for itself. The name changes; the
   * code never does — it is on every instance, audit row and URL. */
  renameAdhocTemplate: (
    documentType: string,
    body: { preflight: false; title: string; title_hi?: string },
  ) => request<TemplateDetail>('POST', `/templates/${documentType}/draft/rename`, body),

  /** What this document is, who reads it, and how this office writes it —
   * carried into every drafting call for this document type. */
  writeDocumentContext: (documentType: string, body: DocumentContextBody) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/context`, body),

  /** Propose a better instruction, or a better line of office text. Writes
   * nothing — the officer accepts or rejects the diff. */
  polishWriting: (body: PolishBody) =>
    request<PolishProposal>('POST', '/templates/polish', body),

  /** Where a per-case block's words come from — declared, never inferred.
   * Which paragraphs a machine wrote is what an officer answers for. */
  setBlockWriter: (
    documentType: string,
    body: { preflight: false; order: number; writes: 'record' | 'model' | 'quote' },
  ) => request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/type`, body),

  /** The one question: what is this paragraph? Six answers, of which five are
   * offered — `statutory` is refused by the server, because statutory text is
   * reproduced from the rules and not created by an editor. */
  setBlockKind: (
    documentType: string,
    body: {
      preflight: false
      order: number
      kind: 'office' | 'office_filled' | 'composed' | 'quoted' | 'stationery'
      language?: 'hi' | 'en'
    },
  ) => request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/kind`, body),

  /** One paragraph's shape, from the six the office uses. `null` clears it and
   * the paragraph renders as it always has. */
  setBlockStyle: (
    documentType: string,
    body: { preflight: false; order: number; style: string | null; language?: 'hi' | 'en' },
  ) => request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/style`, body),

  /** What a model fills in one composed paragraph. The whole set at once: the
   * officer is deciding how the sentence divides, not editing rows. */
  setBlockGaps: (
    documentType: string,
    body: {
      preflight: false
      order: number
      gaps: { key: string; instruction: string }[]
      language?: 'hi' | 'en'
    },
  ) => request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/gaps`, body),

  /** Render the whole open draft against a real case. Writes nothing — the
   * only way to catch a fault that belongs to no single block. */
  previewTemplateDocument: (
    documentType: string,
    body: { preflight: false; case_id: string; language?: 'hi' | 'en' },
  ) => request<DocumentPreview>('POST', `/templates/${documentType}/draft/preview`, body),

  /** Shift a block up or down. Statutory text is locked; its position is not. */
  moveTemplateBlock: (
    documentType: string,
    body: { preflight: false; order: number; direction: 'up' | 'down' },
  ) => request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/move`, body),

  /** Insert a block into the open draft — authoring, not loading. */
  addTemplateBlock: (documentType: string, body: BlockAddBody) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/add`, body),

  /** Remove a block from the open draft. `[S]` is refused. */
  removeTemplateBlock: (documentType: string, body: { preflight: false; order: number }) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/remove`, body),

  /** Empty the draft to a blank page. `[S]` survives; the response says what stayed. */
  clearTemplateBlocks: (documentType: string) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/blocks/clear`, {
      preflight: false,
    }),

  /** Cut one block in two, so each half can carry its own instruction. */
  splitTemplateBlock: (
    documentType: string,
    body: { preflight: false; order: number; at: number },
  ) => request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/split`, body),

  /** Render one block of the draft against a real case. Writes nothing. */
  previewTemplateBlock: (documentType: string, body: BlockPreviewBody) =>
    request<BlockPreview>('POST', `/templates/${documentType}/draft/block/preview`, body),

  setBlockProvenance: (documentType: string, body: ProvenanceBody) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/block/provenance`, body),

  declareTemplateSlot: (documentType: string, body: SlotBody) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/slot`, body),

  writeTemplateCheck: (documentType: string, body: CheckBody) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/check`, body),

  publishTemplate: (documentType: string, body: PublishBody) =>
    request<TemplateDetail>('POST', `/templates/${documentType}/draft/publish`, body),

  discardTemplateDraft: (documentType: string) =>
    request<void>('POST', `/templates/${documentType}/draft/discard`),

  reloadTemplateLibrary: () =>
    request<ReloadOutcomeView[]>('POST', '/templates/reload-from-skeleton'),

  readFactorySettings: () => request<FactorySettings>('GET', '/factory'),

  updateFactorySettings: (body: { default_document_language: string }) =>
    request<FactorySettings>('POST', '/factory', body),

  listOrgBlocks: () => request<OrgBlockView[]>('GET', '/org-blocks'),

  openOrgBlockDraft: (key: string) => request<OrgBlockView>('POST', `/org-blocks/${key}/draft`),

  editOrgBlock: (key: string, body: OrgBlockEditBody) =>
    request<OrgBlockView>('POST', `/org-blocks/${key}/draft/edit`, body),

  publishOrgBlock: (key: string, body: PublishBody) =>
    request<OrgBlockView>('POST', `/org-blocks/${key}/draft/publish`, body),

  confirmOrgBlock: (id: string) => request<OrgBlockView>('POST', `/org-blocks/${id}/confirm`),

  listNamedChecks: () => request<NamedCheckView[]>('GET', '/named-checks'),

  /**
   * **The one place a condition becomes English.** Server-side, always: a
   * second rendering in TypeScript would be a second vocabulary, and it would
   * drift the first time an operator's wording changed — in the direction where
   * a lawyer reviews a sentence the engine does not mean.
   */
  explainCondition: (condition: unknown) =>
    request<ConditionView>('POST', '/conditions/explain', { condition }),

  conditionVocabulary: () => request<ConditionVocabulary>('GET', '/conditions/vocabulary'),

  /* -------------------------------------------------- the process editor -- */

  listProcessVersions: () => request<ProcessVersionRow[]>('GET', '/process/versions'),

  getProcess: () => request<ProcessDetail>('GET', '/process'),

  getProcessVersion: (version: number) =>
    request<ProcessDetail>('GET', `/process/versions/${version}`),

  getProcessDiff: (fromVersion: number, toVersion: number) =>
    request<ProcessDiff>(
      'GET',
      `/process/diff?from_version=${fromVersion}&to_version=${toVersion}`,
    ),

  openProcessDraft: () => request<ProcessDetail>('POST', '/process/draft'),

  editStage: (body: StageEditBody) =>
    request<ProcessDetail>('POST', '/process/draft/stage', body),

  editProcessDocument: (body: DocumentEditBody) =>
    request<ProcessDetail>('POST', '/process/draft/document', body),

  editClock: (body: ClockEditBody) =>
    request<ProcessDetail>('POST', '/process/draft/clock', body),

  editGuard: (body: GuardEditBody) =>
    request<ProcessDetail>('POST', '/process/draft/guard', body),

  publishProcess: (body: PublishBody) =>
    request<ProcessDetail>('POST', '/process/draft/publish', body),

  discardProcessDraft: () => request<void>('POST', '/process/draft/discard'),

  previewCaseMigration: (caseId: string, toVersion: number) =>
    request<MigrationPreview>(
      'GET',
      `/cases/${caseId}/migration-preview?to_version=${toVersion}`,
    ),

  /* -------------------------------------------------------------- OCR tool */

  createOcrToolJob: async (
    file: File,
    fields: { officer_name: string; document_type?: string; other_description?: string },
  ): Promise<OcrToolJobView> => {
    const form = new FormData()
    form.append('file', file)
    for (const [key, value] of Object.entries(fields)) {
      if (value) form.append(key, value)
    }
    const token = csrfToken()
    const response = await fetch(`${BASE}/ocr-tool/jobs`, {
      method: 'POST',
      headers: token
        ? { [CSRF_HEADER]: token, Accept: 'application/json' }
        : { Accept: 'application/json' },
      credentials: 'include',
      body: form,
    })
    if (!response.ok) {
      let payload: ApiError
      try {
        payload = (await response.json()) as ApiError
      } catch {
        payload = { error: response.statusText || 'The upload failed.', missing: [] }
      }
      throw new ApiFailure(response.status, payload)
    }
    return (await response.json()) as OcrToolJobView
  },

  getOcrToolJob: (jobId: string) => request<OcrToolJobView>('GET', `/ocr-tool/jobs/${jobId}`),

  discardOcrToolJob: (jobId: string) => request<void>('DELETE', `/ocr-tool/jobs/${jobId}`),

  /**
   * Fetch the Word document as a blob, with the server's filename. Fetched
   * rather than linked so the caller knows the bytes actually arrived before
   * it confirms receipt with the DELETE — a plain anchor cannot tell a saved
   * file from a dropped connection.
   */
  downloadOcrToolDocument: async (jobId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(`${BASE}/ocr-tool/jobs/${jobId}/document`, {
      credentials: 'include',
    })
    if (!response.ok) {
      let payload: ApiError
      try {
        payload = (await response.json()) as ApiError
      } catch {
        payload = { error: response.statusText || 'The download failed.', missing: [] }
      }
      throw new ApiFailure(response.status, payload)
    }
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1]
    const filename = encoded ? decodeURIComponent(encoded) : 'document.docx'
    return { blob: await response.blob(), filename }
  },

  /* -------------------------------------- Phase A: intake (docs/24 Lane I) --- */

  /** The confirm screen, built from the paper's one reading. No model call and
   * no translation behind it — safe to poll. */
  getIntake: (caseId: string) => request<IntakeView>('GET', `/cases/${caseId}/intake`),

  /** One small call for one missing value, never twice for the same reading. */
  askIntakeAgain: (caseId: string, body: AskAgainBody, o?: RequestOptions) =>
    request<AskAgainReceipt>('POST', `/cases/${caseId}/intake/ask-again`, body, o),

  /** The officer's one confirming act: his record written, the number taken,
   * the acts the officer confirmed recorded through the one transition path. */
  confirmCase: (caseId: string, body: ConfirmCaseBody, o?: RequestOptions) =>
    request<CaseConfirmReceipt>('POST', `/cases/${caseId}/intake/confirm`, body, o),

  /** The identifier line of every case the register lists — English, built on
   * the server, never composed here. */
  listCaseIdentifiers: () => request<CaseIdentifierView[]>('GET', '/cases/identifiers'),

  getCaseIdentifier: (caseId: string) =>
    request<CaseIdentifierView>('GET', `/cases/${caseId}/identifier`),
}

/* ------------------------------------------------------------------------- */
/* Phase A, Lane C — the case page: "Needs a look", his particulars corrected */
/* on the case page, and a line the app missed. Every type is generated.     */
/* Kept in one block at the end so the lanes merge cleanly.                  */
/* ------------------------------------------------------------------------- */

export type NeedsALookLine = components['schemas']['NeedsALookLine']
export type ParticularsView = components['schemas']['ParticularsView']
export type ParticularFieldView = components['schemas']['ParticularFieldView']
export type ParticularSightingView = components['schemas']['ParticularSightingView']
export type ParticularChangeView = components['schemas']['ParticularChangeView']
export type ParticularCorrectionReceipt = components['schemas']['ParticularCorrectionReceipt']
export type ParticularsSinceView = components['schemas']['ParticularsSinceView']
export type PaperPagesView = components['schemas']['PaperPagesView']
export type LineKindOption = components['schemas']['LineKindOption']
export type CorrectParticularBody = Body<'/api/cases/{case_id}/particulars/correct', 'post'>
export type AddRecordLineBody = Body<
  '/api/cases/{case_id}/source-files/{file_id}/record/add',
  'post'
>

export const casePageApi = {
  /** His particulars: as his record holds them, the app's reading beneath each,
   *  what the papers give for each, and every correction — from any of his cases. */
  getParticulars: (caseId: string) =>
    request<ParticularsView>('GET', `/cases/${caseId}/particulars`),

  /** One of his particulars corrected, with the reason. His record is shared,
   *  so the correction reaches his other cases; the old value stays beneath. */
  correctParticular: (caseId: string, body: CorrectParticularBody) =>
    request<ParticularCorrectionReceipt>('POST', `/cases/${caseId}/particulars/correct`, body),

  /** Whether his particulars changed after this version of a document was made. */
  particularsSinceVersion: (caseId: string, versionId: string) =>
    request<ParticularsSinceView>(
      'GET',
      `/cases/${caseId}/particulars/since-version/${versionId}`,
    ),

  /** A paper's read text, page by page, and the kinds of line it takes — decided
   *  on the server, never here. */
  getPaperPages: (caseId: string, fileId: string) =>
    request<PaperPagesView>('GET', `/cases/${caseId}/source-files/${fileId}/pages`),

  /** A line the reading missed: the words as on the page, kept as their whole
   *  sentence, with one English line and the reason. */
  addRecordLine: (caseId: string, fileId: string, body: AddRecordLineBody) =>
    request<RecordItemReceipt>(
      'POST',
      `/cases/${caseId}/source-files/${fileId}/record/add`,
      body,
    ),
}
